import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { SpaceMemberRead } from '@models/api';
import { MEMBERS_LIST_ALL_OPEN, SpaceMemberStatus } from '@models/api/space';
import type { MembersInfiniteData } from './memberCacheTypes';
import { spaceMembersKey } from './queryKeys';

/**
 * Statuses each members-list status segment (`queryKey[2]`) holds. A row whose optimistic status
 * leaves its list's set is dropped until the refetch; `all` and unknown segments keep every row.
 */
const LIST_STATUSES: Record<string, readonly string[]> = {
  approved: [SpaceMemberStatus.approved],
  [MEMBERS_LIST_ALL_OPEN]: [SpaceMemberStatus.approved, SpaceMemberStatus.pending],
  pending: [SpaceMemberStatus.pending],
  declined: [SpaceMemberStatus.declined],
  blocked: [SpaceMemberStatus.blocked],
};

/** Snapshot of every cached members variant for a space, captured for optimistic rollback. */
export type MembersCacheSnapshot = [QueryKey, MembersInfiniteData | undefined][];

/**
 * Optimistically transform every cached members page for a space and return a
 * snapshot for rollback.
 *
 * Extracted so the cancel + snapshot + `setQueriesData` + `{ pages }` shape
 * guard live in ONE place instead of being copied across the member-row
 * mutations (`useChangeMemberRole`, `useChangeMemberStatus`'s status + remove
 * mutations, `useSetMemberHat`). A change to the members-cache shape or the
 * rollback contract now lands here once rather than in four hooks.
 *
 * Callers supply only how a page's rows change: `transformRows` receives a
 * page's `data` array and returns the new one — use `.map` to patch a field
 * (role / status / hat) or `.filter` to remove a row. Rows whose new status
 * falls outside an entry's status segment are then dropped from that entry.
 */
export async function beginOptimisticMembersUpdate(
  queryClient: QueryClient,
  spaceId: string,
  transformRows: (rows: SpaceMemberRead[]) => SpaceMemberRead[],
): Promise<{ snapshot: MembersCacheSnapshot }> {
  await queryClient.cancelQueries({ queryKey: spaceMembersKey(spaceId) });
  const snapshot = queryClient.getQueriesData<MembersInfiniteData>({
    queryKey: spaceMembersKey(spaceId),
  });
  for (const [key, existing] of snapshot) {
    // AIDEV-NOTE: silent skip when cache shape is unexpected. Cache refactors
    // must preserve { pages, pageParams } or update this guard — otherwise
    // optimistic member-row updates silently stop reflecting in the UI.
    if (!existing?.pages) continue;
    const segment = key[2];
    const allowed = typeof segment === 'string' ? LIST_STATUSES[segment] : undefined;
    queryClient.setQueryData<MembersInfiniteData>(key, {
      ...existing,
      pages: existing.pages.map(page => {
        const rows = transformRows(page.data);
        return { ...page, data: allowed ? rows.filter(m => allowed.includes(m.status)) : rows };
      }),
    });
  }
  return { snapshot };
}

/** Restore a snapshot captured by {@link beginOptimisticMembersUpdate} (error rollback). */
export function rollbackMembersUpdate(
  queryClient: QueryClient,
  snapshot: MembersCacheSnapshot | undefined,
): void {
  snapshot?.forEach(([key, data]) => {
    if (data) queryClient.setQueryData(key, data);
  });
}
